import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircleIcon } from "@primer/octicons-react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../api/client";
import { useWhoami } from "../api/hooks";
import { ErrorMessage, Loading, Page } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

interface DeviceRequest {
  user_code: string;
  user_agent: string;
  ip: string;
  created: number;
}

/** Normalizes what a person types: "abcd efgh" and "ABCD-EFGH" are the same code. */
const clean = (s: string) => {
  const c = s.toUpperCase().replace(/[^A-Z0-9]/g, "");
  return c.length > 4 ? `${c.slice(0, 4)}-${c.slice(4, 8)}` : c;
};

/** Approves a `bee login` started on another machine (device flow). */
export function Device() {
  const [params, setParams] = useSearchParams();
  const code = clean(params.get("code") ?? "");
  const who = useWhoami();
  const [typed, setTyped] = useState("");
  const full = code.length === 9;
  const req = useQuery({
    queryKey: ["device", code],
    enabled: full,
    retry: false,
    queryFn: () => api.get<DeviceRequest>(`/v1/device/${encodeURIComponent(code)}`),
  });
  const answer = useMutation({
    mutationFn: (approve: boolean) => api.send<{ approved: boolean }>("POST", "/v1/device/answer", { user_code: code, approve }),
  });
  const done = answer.data;

  return (
    <Page>
      <h1 className="mb-2 mt-6 text-2xl">Sign in the bee CLI</h1>
      {!full ? (
        <form
          className="max-w-sm space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            setParams({ code: clean(typed) });
          }}
        >
          <p>Enter the code that bee login printed in your terminal.</p>
          <Input mono autoFocus value={typed} onChange={(e) => setTyped(clean(e.target.value))} placeholder="ABCD-EFGH" maxLength={9} />
          <Button variant="primary" disabled={clean(typed).length !== 9}>
            Continue
          </Button>
        </form>
      ) : req.isLoading || who.isLoading ? (
        <Loading />
      ) : req.error ? (
        <ErrorMessage error={req.error} />
      ) : done ? (
        <p className="flex items-center gap-2">
          <CheckCircleIcon />
          {done.approved ? "Approved. You can return to your terminal." : "Denied. The CLI was not signed in."}
        </p>
      ) : req.data ? (
        <div className="max-w-lg space-y-3">
          <p>
            Allow the bee CLI to sign in as <strong>{who.data?.sub}</strong>? It will have your access until you sign out.
          </p>
          <div className="rounded-md border border-border-muted bg-muted p-4">
            <div className="font-mono text-2xl tracking-widest">{req.data.user_code}</div>
            <div className="mt-2 text-xs text-muted-foreground">
              Requested {new Date(req.data.created).toLocaleTimeString()} from {req.data.ip || "an unknown address"}
              {req.data.user_agent ? ` (${req.data.user_agent})` : ""}.
            </div>
          </div>
          <p className="text-sm text-muted-foreground">Approve only if this code matches the one in your terminal and you started bee login yourself.</p>
          <div className="flex gap-2">
            <Button variant="primary" disabled={answer.isPending} onClick={() => answer.mutate(true)}>
              Approve
            </Button>
            <Button disabled={answer.isPending} onClick={() => answer.mutate(false)}>
              Deny
            </Button>
          </div>
          {answer.error ? <ErrorMessage error={answer.error} /> : null}
        </div>
      ) : null}
    </Page>
  );
}
